import { useState, useCallback } from 'react';
import { useLoader } from '../contexts/LoaderContext';
import { useAlert } from '../contexts/AlertContext';
import { MerchListResponse, merch } from '../api/merch';

interface UseMerchApiParams {
  agencyFilter: string;
  categoryFilter: string;
  currentPage: number;
  itemsPerPage: number;
}

export const useMerchApi = ({
  agencyFilter,
  categoryFilter,
  currentPage,
  itemsPerPage,
}: UseMerchApiParams) => {
  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = useAlert();

  const [merchData, setMerchData] = useState<MerchListResponse>({
    merch: [],
    pageNumber: 1,
    pageSize: itemsPerPage,
    totalCount: 0,
    totalPages: 0,
  });

  const fetchMerch = useCallback(async () => {
    showLoader();
    try {
      const params = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        agencyId: agencyFilter ? parseInt(agencyFilter) : undefined,
        merchCategoryId: categoryFilter ? parseInt(categoryFilter) : undefined,
      };

      const response = await merch.getList(params);
      setMerchData(response);
      hideLoader();
    } catch (error) {
      showAlert('Failed to load merch', 'error');
      hideLoader();
    }
  }, [agencyFilter, categoryFilter, currentPage, itemsPerPage, showAlert, showLoader, hideLoader]);

  const deleteMerch = useCallback(
    async (merchId: number) => {
      try {
        showLoader();
        await merch.delete(merchId);
        await fetchMerch();
        showAlert('Merch deleted successfully', 'success');
      } catch (error) {
        showAlert('Failed to delete merch', 'error');
      } finally {
        hideLoader();
      }
    },
    [showLoader, hideLoader, showAlert, fetchMerch]
  );

  return {
    merchData,
    fetchMerch,
    deleteMerch,
  };
};
